import FormGroup from "@/components/ui/FormGroup";
import Modal from "@/components/ui/Modal";
import { editEvent, fetchEvents } from "@/utils/eventService";
import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import Flatpickr from "react-flatpickr";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";

import { ImageUploadInput } from "./ImageUpload";
import { resetMessage, toggleEditModal } from "./store";

const EditEvent = () => {
  const { editModal, editItem, message } = useSelector((state) => state.event);
  const dispatch = useDispatch();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // robust parser for "YYYY-MM-DD HH:mm" or "YYYY-MM-DD h:mm AM/PM" (local time)
  const parseLocalDateTime = (input) => {
    if (!input) return null;
    if (input instanceof Date) return input;
    if (typeof input !== "string") {
      const d = new Date(input);
      return isNaN(d.getTime()) ? null : d;
    }
    // try native first
    const native = new Date(input);
    if (!isNaN(native.getTime())) return native;

    // trim & split
    const parts = input.trim().split(/\s+/); // ["YYYY-MM-DD","HH:mm"] or ["YYYY-MM-DD","h:mm","AM"]
    if (parts.length < 2) return null;

    const [y, m, d] = parts[0].split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;

    let hh = 0;
    let mm = 0;

    const timePart = parts[1] || "00:00";
    const [hStr, minStr] = timePart.split(":");
    if (hStr == null || minStr == null) return null;

    hh = parseInt(hStr, 10);
    mm = parseInt(minStr, 10) || 0;

    // AM/PM?
    const meridian = (parts[2] || "").toUpperCase();
    if (meridian === "AM" || meridian === "PM") {
      if (meridian === "PM" && hh < 12) hh += 12;
      if (meridian === "AM" && hh === 12) hh = 0;
    }

    const out = new Date(y, m - 1, d, hh, mm, 0, 0);
    return isNaN(out.getTime()) ? null : out;
  };

  // helper: format local datetime => "YYYY-MM-DD HH:mm"
  const formatLocalDateTime = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
  };

  const FormValidationSchema = yup.object({
    name: yup.string().required("Name is required"),
    imageUrl: yup.string(),
    costPerVote: yup
      .number()
      .typeError("Must be a number")
      .required("Cost per vote is required"),
    resultsVisibility: yup.boolean().required(),
    sortByVotes: yup.boolean(),
    startAt: yup
      .date()
      .required("Start date & time is required")
      .min(today, "Start must be today or later"),
    endAt: yup
      .date()
      .required("End date & time is required")
      .min(yup.ref("startAt"), "End must be after start"),
    voteDisplayOption: yup
      .string()
      .oneOf(["card", "table", "bar_chart"])
      .required("Vote display option is required"),
  });

  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(FormValidationSchema),
    mode: "all",
    defaultValues: {
      name: "",
      costPerVote: "",
      resultsVisibility: true,
      sortByVotes: false,
      voteDisplayOption: "card",
      startAt: today,
      endAt: new Date(today.getTime() + 60 * 60 * 1000),
    },
  });

  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    if (editItem) {
      const parsedStart = parseLocalDateTime(editItem.startDate) ?? today;
      const parsedEnd =
        parseLocalDateTime(editItem.endDate) ??
        new Date(today.getTime() + 60 * 60 * 1000);

      reset({
        name: editItem.title,
        costPerVote: editItem.costPerVote,
        resultsVisibility: editItem.resultsVisibility ?? true,
        sortByVotes: editItem.sortByVotes ?? false,
        voteDisplayOption: editItem.voteDisplayOption || "card",
        startAt: parsedStart,
        endAt: parsedEnd,
      });
      setImageUrl(editItem.imageUrl || "");
    }
  }, [editItem, reset]);

  const onSubmit = async (data) => {
    const eventData = {
      title: data.name,
      costPerVote: data.costPerVote,
      imageUrl,
      startDate: formatLocalDateTime(data.startAt),
      endDate: formatLocalDateTime(data.endAt),
      hasBulkVoting: false,
      paymentMethods: ["1"],
      isActive: true,
      resultsVisibility: data.resultsVisibility,
      sortByVotes: data.sortByVotes,
      voteDisplayOption: data.voteDisplayOption,
    };

    dispatch(editEvent({ eventId: editItem.id, event: eventData }));
    dispatch(toggleEditModal(false));
  };

  useEffect(() => {
    if (message) {
      toast.success(message);
      dispatch(fetchEvents());
      dispatch(resetMessage());
    }
  }, [message]);

  const handleImageUrlChange = (url) => setImageUrl(url);

  // for dynamic minDate on endAt
  const startAtValue = watch("startAt") || today;

  return (
    <Modal
      title="Edit Event"
      activeModal={editModal}
      onClose={() => dispatch(toggleEditModal(false))}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormGroup error={errors.name}>
          <label htmlFor="name">Event name</label>
          <input
            type="text"
            placeholder="Enter Event name"
            className="form-control py-2"
            {...register("name")}
          />
        </FormGroup>

        <FormGroup error={errors.costPerVote}>
          <label htmlFor="costPerVote">Cost per Vote</label>
          <input
            type="number"
            step="0.01"
            className="form-control py-2"
            {...register("costPerVote")}
          />
        </FormGroup>

        <FormGroup>
          <ImageUploadInput
            label="Image URL"
            name="imageUrl"
            defaultValue={imageUrl}
            onImageUrlChange={handleImageUrlChange}
            imageUrl={imageUrl}
          />
        </FormGroup>

        <FormGroup error={errors.resultsVisibility}>
          <label htmlFor="resultsVisibility">Display Votes</label>
          <input
            type="checkbox"
            id="resultsVisibility"
            {...register("resultsVisibility")}
          />
        </FormGroup>

        <FormGroup error={errors.sortByVotes}>
          <label htmlFor="sortByVotes">Sort Nominees by Votes </label>
          <input
            type="checkbox"
            id="sortByVotes"
            {...register("sortByVotes")}
          />
        </FormGroup>

        <FormGroup error={errors.voteDisplayOption}>
          <label htmlFor="voteDisplayOption">Display Votes As</label>
          <select
            className="form-control py-2"
            {...register("voteDisplayOption")}
          >
            <option value="card">Card</option>
            <option value="table">Table</option>
            <option value="bar_chart">Bar Chart</option>
          </select>
        </FormGroup>

        {/* Combined DateTimes with AM/PM */}
        <div className="grid lg:grid-cols-2 gap-4 grid-cols-1">
          <FormGroup label="Start (Date & Time)" error={errors.startAt}>
            <Controller
              control={control}
              name="startAt"
              render={({ field }) => (
                <Flatpickr
                  key={`start-${editItem?.id || "new"}`} // force refresh on item change
                  className="form-control py-2"
                  value={field.value}
                  onChange={(date) => field.onChange(date[0])}
                  options={{
                    enableTime: true,
                    time_24hr: false, // 12-hour mode
                    altInput: true,
                    altFormat: "F j, Y - h:i K", // AM/PM visible
                    dateFormat: "Y-m-d h:i K", // input parsing format
                    minuteIncrement: 1,
                    minDate: today,
                  }}
                />
              )}
            />
          </FormGroup>

          <FormGroup label="End (Date & Time)" error={errors.endAt}>
            <Controller
              control={control}
              name="endAt"
              render={({ field }) => (
                <Flatpickr
                  key={`end-${editItem?.id || "new"}`}
                  className="form-control py-2"
                  value={field.value}
                  onChange={(date) => field.onChange(date[0])}
                  options={{
                    enableTime: true,
                    time_24hr: false,
                    altInput: true,
                    altFormat: "F j, Y - h:i K",
                    dateFormat: "Y-m-d h:i K",
                    minuteIncrement: 1,
                    minDate: startAtValue || today,
                  }}
                />
              )}
            />
          </FormGroup>
        </div>

        <div className="ltr:text-right rtl:text-left">
          <button className="btn btn-dark">Update</button>
        </div>
      </form>
    </Modal>
  );
};

export default EditEvent;
